import { AI_NOT_ALLOWED_NOTE } from "../ai/aiNotAllowed";
import type { WireMessage } from "./agentMemory";
import { READ_CONVERSATION_FILE_TOOL_NAME } from "./tools";

export const CONVERSATION_FILE_EXTENSIONS = ["pdf", "docx"] as const;

export const MAX_CONVERSATION_FILE_MB = 20;

const MAX_CONVERSATION_FILE_BYTES = MAX_CONVERSATION_FILE_MB * 1024 * 1024;

export const CONVERSATION_FILE_ACCEPT = CONVERSATION_FILE_EXTENSIONS.map((extension) => `.${extension}`).join(",");

export type ConversationAttachment = { documentId: string; fileName: string };

export type ConversationFileRefusal = "unsupportedType" | "tooLarge";

export const extensionOf = (fileName: string): string => {
  const dot = fileName.lastIndexOf(".");
  return dot < 0 ? "" : fileName.slice(dot + 1).toLowerCase();
};

export const conversationFileRefusal = (file: { name: string; size: number }): ConversationFileRefusal | null => {
  if (!(CONVERSATION_FILE_EXTENSIONS as readonly string[]).includes(extensionOf(file.name))) {
    return "unsupportedType";
  }
  return file.size > MAX_CONVERSATION_FILE_BYTES ? "tooLarge" : null;
};

export const withAttachments = (text: string, attachments: ConversationAttachment[]): string => {
  if (attachments.length === 0) {
    return text;
  }
  const files = attachments.map((file) => `- ${file.fileName} (documentId: ${file.documentId})`).join("\n");
  const question = text || "Les filen og fortell kort hva den inneholder.";
  return `${question}\n\nVedlagte filer (les dem med ${READ_CONVERSATION_FILE_TOOL_NAME}):\n${files}`;
};

const FILE_NOT_ALLOWED_NOTE =
  "Organisasjonens KI-innstilling tillater ikke å lese vedlagte filer: de leses på AWS Bedrock i EU eller hos OpenAI, " +
  "og ingen av dem er tillatt. " +
  "Filen er ikke lest. Du kan fortsette samtalen uten den.";

// The BFF keeps any request whose messages contain this marker on the endpoint attached files are read on
// (CONVERSATION_FILE_TEXT_MARKER in ConversationFileReading.kt), so it must match that text exactly.
export const CONVERSATION_FILE_TEXT_MARKER = "[Vedlagt fil – leses bare på AWS Bedrock i EU]";

export const readsConversationFiles = (messages: readonly WireMessage[]): boolean =>
  messages.some(
    (message) =>
      message.toolCalls?.some((call) => call.name === READ_CONVERSATION_FILE_TOOL_NAME) ||
      message.content?.includes(CONVERSATION_FILE_TEXT_MARKER),
  );

export const aiSettingRefusalNote = (
  attachments: readonly ConversationAttachment[],
  turnMessages: readonly WireMessage[],
): string => (attachments.length > 0 || readsConversationFiles(turnMessages) ? FILE_NOT_ALLOWED_NOTE : AI_NOT_ALLOWED_NOTE);
